import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar";
import { toast } from "react-hot-toast";
import Notice from "../Notice";
import Student from "./Student";
import Faculty from "./Faculty";
import Subjects from "./Subject";
import Admin from "./Admin";
import Branch from "./Branch";
import { useDispatch } from "react-redux";
import { setUserData } from "../../redux/actions";
import axiosWrapper from "../../utils/AxiosWrapper";
import Profile from "./Profile";
import Exam from "../Exam";
import { useNavigate, useLocation } from "react-router-dom";
import ChatWindow from "../../ChatWindow";
import Leads from "./Leads";
import MethodPaperSubSubjects from "./MethodPaperSubSubjects";
import BedPracticeSets from "./BedPracticeSets";

/** All admin sections — mobile uses a dropdown so none are clipped or missed. */
const MENU_ITEMS = [
  { id: "home", label: "Home", component: Profile },
  { id: "student", label: "Student", component: Student },
  { id: "faculty", label: "Faculty", component: Faculty },
  { id: "branch", label: "Branch", component: Branch },
  { id: "notice", label: "Notice", component: Notice },
  { id: "exam", label: "Exam", component: Exam },
  { id: "subjects", label: "Subjects", component: Subjects },
  {
    id: "bed-practice-sets",
    label: "B.Ed practice (MCQs)",
    component: BedPracticeSets,
  },
  {
    id: "method-paper-sub-subjects",
    label: "MP Sub-subjects",
    component: MethodPaperSubSubjects,
  },
  { id: "leads", label: "Leads", component: Leads },
  { id: "admin", label: "Admin", component: Admin },
  { id: "chat", label: "Chat", component: ChatWindow },
];

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMenu, setSelectedMenu] = useState("home");
  const [profileData, setProfileData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const userToken = localStorage.getItem("userToken");

  const fetchUserDetails = async () => {
    setIsLoading(true);
    try {
      toast.loading("Loading user details...");
      const response = await axiosWrapper.get(`/admin/my-details`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      if (response.data.success) {
        setProfileData(response.data.data);
        dispatch(setUserData(response.data.data));
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Error fetching user details"
      );
    } finally {
      setIsLoading(false);
      toast.dismiss();
    }
  };

  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userToken]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const pathMenuId = urlParams.get("page") || "home";
    const validMenu = MENU_ITEMS.find((item) => item.id === pathMenuId);
    setSelectedMenu(validMenu ? validMenu.id : "home");
  }, [location.pathname, location.search]);

  const getMenuItemClass = (menuId) => {
    const isSelected = selectedMenu === menuId;
    return `
      whitespace-nowrap text-center px-3 sm:px-4 py-2 sm:py-2.5 cursor-pointer
      font-medium text-xs sm:text-sm shrink-0
      rounded-lg border border-transparent max-w-[100%]
      transition-all duration-200 ease-in-out select-none
      ${
        isSelected
          ? "bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-md ring-2 ring-blue-400/40"
          : "bg-blue-50 dark:bg-slate-800 text-blue-900 dark:text-blue-100 border-blue-100 dark:border-slate-600 hover:bg-blue-100 dark:hover:bg-slate-700 hover:border-blue-200 dark:hover:border-slate-500"
      }
    `;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center min-h-[40vh] text-slate-600 dark:text-slate-400">
          Loading…
        </div>
      );
    }

    const MenuItem = MENU_ITEMS.find(
      (item) => item.id === selectedMenu
    )?.component;

    if (selectedMenu === "home" && profileData) {
      return <Profile profileData={profileData} />;
    }

    return MenuItem ? (
      <div className="w-full min-w-0 overflow-x-hidden">
        <MenuItem />
      </div>
    ) : null;
  };

  const handleMenuClick = (menuId) => {
    setSelectedMenu(menuId);
    navigate(`/admin?page=${menuId}`);
  };

  return (
    <div className="min-h-screen w-full max-w-[100vw] overflow-x-hidden bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-200">
      <Navbar />
      <main className="w-full max-w-[100vw] min-w-0 px-3 sm:px-4 pb-16">
        <div className="max-w-7xl mx-auto w-full min-w-0 pt-4">
          {/* Mobile + small tablet: dropdown — every tab is visible (fixes missing B.Ed practice). */}
          <div className="md:hidden sticky top-0 z-40 -mx-1 px-1 py-2 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-700/80 mb-4">
            <label
              htmlFor="admin-mobile-section"
              className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5"
            >
              Go to section
            </label>
            <select
              id="admin-mobile-section"
              value={MENU_ITEMS.some((x) => x.id === selectedMenu) ? selectedMenu : "home"}
              onChange={(e) => handleMenuClick(e.target.value)}
              className="w-full min-w-0 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-3 text-base font-semibold text-slate-900 dark:text-slate-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {MENU_ITEMS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          {/* lg+: pill grid */}
          <nav
            className="hidden md:block w-full min-w-0 mb-8"
            aria-label="Admin sections"
          >
            <ul className="flex flex-wrap justify-center gap-2 xl:gap-2.5 w-full">
              {MENU_ITEMS.map((item) => (
                <li key={item.id} className="list-none shrink-0 max-w-[100%]">
                  <button
                    type="button"
                    className={getMenuItemClass(item.id)}
                    onClick={() => handleMenuClick(item.id)}
                    aria-current={
                      selectedMenu === item.id ? "page" : undefined
                    }
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="w-full min-w-0">{renderContent()}</div>
        </div>
      </main>
    </div>
  );
};

export default Home;
